import type {
	Application,
	Currency
} from "@nwt/norce-pwa/dist/runtime/types/api/application/application";
import type {
	ParametricListValue,
	ProductType
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { defineStore } from "pinia";

interface State {
	cultureCode: string;
	localeCode: string;
	/*
		The shopper's market, kept per locale rather than as one value.

		Each locale is a different Norce application with its own sales areas and
		its own price lists, so one slot shared between them can only ever hold
		another store's answer. It used to, with a stamp saying which store it came
		from — but the stamp was written only when a country was chosen, never when
		`initState` settled on a default, so a Swedish default travelled into the EU
		store unstamped and could not be told apart from a deliberate choice.
		Keeping a key per locale removes the question rather than answering it:
		there is no value to validate, only one that is either present for this
		store or absent.

		It also means a shopper who picks Austria on the EU store, visits the
		Swedish one and comes back finds Austria still chosen — before the first
		request goes out, rather than after the basket corrects it.
	*/
	salesAreaByLocale: Record<string, number>;
	countryCodeByLocale: Record<string, string>;
	paymentAdapter: string;
	checkoutChannel: string | undefined;
	currencyId: number;
	currenciesNorce: Currency[];
	currentCurrency: Currency | null;
	applicationNorce: Application | null;
	parametrics: ParametricsNorceStore;
	productTypes: ProductType[];
}

interface ParametricsNorceStore {
	[key: string]: ParametricListValue[];
}
export const useNorceStore = defineStore("norceStore", {
	state: (): State => ({
		cultureCode: "",
		localeCode: "",
		salesAreaByLocale: {},
		countryCodeByLocale: {},
		paymentAdapter: "",
		checkoutChannel: undefined,
		currencyId: 0,
		currenciesNorce: [],
		currentCurrency: null,
		applicationNorce: null,
		parametrics: {},
		productTypes: []
	}),
	getters: {
		/**
		 * The market recorded for the store being browsed, or nothing.
		 *
		 * Reading through the locale is what makes another store's answer
		 * unreachable rather than merely detectable: Spain's EUR area is still in
		 * the map, under `global`, and simply is not what `se` asks for.
		 */
		salesAreaId: (state): number | undefined =>
			state.salesAreaByLocale[state.localeCode],

		/** The delivery country chosen for this store at checkout, if any. */
		countryCode: (state): string =>
			state.countryCodeByLocale[state.localeCode] ?? "",

		/**
		 * The price lists to quote with, worked out from the shopper's sales area
		 * on every request rather than remembered.
		 *
		 * No seed is read back from the browser's stored state, which is the point:
		 * one kept between page loads outlives the store it was chosen in, and
		 * that is how Spain's EUR lists came to price a Swedish order. The area is
		 * the one thing worth remembering; the lists follow from it, and from the
		 * shopper's own terms where they have any.
		 *
		 * Saying it explicitly also asks Norce for the same prices it would have
		 * chosen anyway, while giving an order that was priced from the wrong
		 * market something concrete to be corrected by — sending nothing corrects
		 * nothing.
		 *
		 * A price list added in Norce reaches shoppers on its own, within the hour
		 * the list response is cached for or as soon as that cache is cleared. No
		 * deploy is involved.
		 */
		priceListSeed(): string | undefined {
			const areaId = this.resolvedSalesAreaId;
			const marketIds =
				areaId === undefined
					? []
					: useProductStore()
							.allPriceLists.filter(
								(priceList) => priceList.SalesAreaId === areaId
							)
							.map((priceList) => priceList.Id);

			const ids = [...new Set([...marketIds, ...this.negotiatedPriceListIds])];

			// Sorted so the same set always spells the same seed. The seed is a
			// query parameter, and a query parameter is part of both the server's
			// cache key and the `useAsyncData` key — two shoppers on identical
			// terms must produce one string, not one per concatenation order.
			return ids.length ? [...ids].sort((a, b) => a - b).join(",") : undefined;
		},

		/**
		 * The signed-in shopper's own price lists, on top of the market's.
		 *
		 * Norce reaches the same prices from either direction: send `customerId` /
		 * `companyId` and it resolves the lists itself, or send the lists and it
		 * prices from those. It has confirmed the two are equivalent, which is why
		 * nothing here identifies the shopper.
		 *
		 * That is not a detail. A request that names the customer produces an
		 * answer only that customer may see, and this storefront is rendered on a
		 * server whose pages the CDN keeps for half an hour and serves to
		 * everyone. Prices expressed as a list of ids stay comparable: the same
		 * terms spell the same seed, so shoppers on the same terms share a cached
		 * page, and only genuinely different prices cost a separate one.
		 *
		 * Company and personal lists are added together rather than one replacing
		 * the other. Norce is explicit that a customer's own lists join the mix
		 * alongside the company's when both ids are sent, so dropping either half
		 * would quote a price Norce never would.
		 */
		negotiatedPriceListIds(): number[] {
			const user = userStore().getUser;
			const companyId = useSelectedCompanyStore().companyId;
			const company =
				companyId == null
					? undefined
					: user?.Companies?.find((candidate) => candidate.Id === companyId);

			return [
				...new Set([
					...(company?.PricelistIds ?? []),
					...(user?.PricelistIds ?? [])
				])
			].sort((a, b) => a - b);
		},

		/**
		 * What tells two shoppers' prices apart, for keying a fetch by.
		 *
		 * The server renders every page as a guest — the session cookie never
		 * reaches the internal API calls — and the CDN keeps that page for half an
		 * hour and hands it to everyone. So a signed-in shopper is first shown the
		 * market's prices and has to be given their own.
		 *
		 * `useAsyncData` reuses the server's payload whenever the key matches, so
		 * the key has to differ exactly when the prices do. A guest's key matches
		 * what the server rendered and nothing is refetched; a shopper with terms
		 * of their own produces a different key and the browser fetches again.
		 */
		priceContextKey(): string {
			const negotiated = this.negotiatedPriceListIds;
			return `${this.resolvedSalesAreaId ?? "default"}:${
				negotiated.length ? negotiated.join("-") : "public"
			}`;
		},

		/**
		 * Whether this application spans more than one market.
		 *
		 * It no longer gates pricing. The area is sent on every request and the
		 * price lists are selected by it everywhere, single-market storefronts
		 * included — one rule, so what the storefront quotes and what it asks
		 * Norce for cannot drift apart.
		 *
		 * What is left is `useSalesArea`, which offers no country picking on an
		 * application that has only one market to pick from.
		 */
		salesAreaEnabled: (state) =>
			(state.applicationNorce?.SalesAreas?.List?.length ?? 0) > 1,

		/** Whether this application offers the given sales area at all. */
		hasSalesArea:
			(state) =>
			(id: number | undefined): boolean =>
				id !== undefined &&
				(state.applicationNorce?.SalesAreas?.List ?? []).some(
					(area) => area.Id === id
				),

		/**
		 * The sales area every price request should carry: the shopper's own once
		 * a country has been chosen, the application's default until then.
		 *
		 * Stating the default rather than omitting it asks Norce for the same
		 * prices either way, but it makes the request say which market it is
		 * priced for. That matters because the server renders without the
		 * shopper's stored area and the browser has it — with the area named on
		 * both, the two requests are comparable, and a page rendered before a
		 * country was chosen can be told apart from one rendered after.
		 */
		resolvedSalesAreaId(): number | undefined {
			// Only an area this application actually offers. Reading by locale
			// already keeps another store's area out, so this covers what that
			// cannot: a locale pointed at a different application than the one it
			// was pointed at when the value was stored.
			const chosen = this.hasSalesArea(this.salesAreaId)
				? this.salesAreaId
				: undefined;

			return chosen ?? this.applicationNorce?.SalesAreas?.Default?.Id;
		},

		storeContext(): {
			cultureCode: string;
			localeCode: string;
			currencyId: number;
			pricelistSeed?: string;
			salesAreaId?: number;
		} {
			return {
				cultureCode: this.cultureCode,
				localeCode: this.localeCode,
				currencyId: this.currencyId,
				// Worked out from the sales area on every request — never read back
				// from what the browser happened to keep. An empty string is not the
				// same as no seed and must never be sent.
				...(this.priceListSeed ? { pricelistSeed: this.priceListSeed } : {}),
				...(this.resolvedSalesAreaId
					? { salesAreaId: this.resolvedSalesAreaId }
					: {})
			};
		},
		checkoutContext: (state) => ({
			localeCode: state.localeCode,
			...(state.checkoutChannel
				? { checkoutChannel: state.checkoutChannel }
				: {})
		}),
		getCurrentCurrency: (state) => {
			return state.currentCurrency;
		},
		allParametrics: (state) => state.parametrics
	},
	actions: {
		setCurrencyId(newCurrencyId: number) {
			this.currencyId = newCurrencyId;
		},
		setCultureCode(newCultureCode: string) {
			this.cultureCode = newCultureCode;
		},
		setLocaleCode(code: string) {
			this.localeCode = code;
		},
		setCountryCode(code?: string) {
			// Recorded against the store it was chosen in. Without a locale there
			// is nothing to record it against, and writing it under `""` would
			// hand it to whichever store loaded first.
			if (!this.localeCode) return;

			if (code) this.countryCodeByLocale[this.localeCode] = code;
			else delete this.countryCodeByLocale[this.localeCode];
		},
		setCheckoutChannel(channel: string | undefined) {
			this.checkoutChannel = channel;
		},
		setPaymentAdapter(adapter: string) {
			this.paymentAdapter = adapter;
		},
		setSalesAreaId(id: number | undefined) {
			if (!this.localeCode) return;

			if (id === undefined) delete this.salesAreaByLocale[this.localeCode];
			else this.salesAreaByLocale[this.localeCode] = id;
		},
		/**
		 * Take the market from a basket that already carries one.
		 *
		 * The area is remembered per locale in this browser, so it survives a trip
		 * to another store and back. What it cannot survive is another browser,
		 * another device, or cleared site data — and there the basket still knows:
		 * its area was written by an explicit choice at checkout and lives on the
		 * server. Without this the shopper would be shown a page priced for
		 * `Europe`, VAT-free, while their basket stayed priced for Belgium.
		 *
		 * A country chosen for this store wins, since it is the newer statement of
		 * the two. When the shopper does change country the basket is brought in
		 * line with it instead, so the two only disagree in the direction this
		 * corrects.
		 */
		adoptSalesAreaFromBasket(basketSalesAreaId: number | undefined) {
			if (!basketSalesAreaId || this.countryCode) return;
			// Same guard as everywhere else: sales area ids come from one table
			// shared by every Norce client, so an id is always a real area
			// somewhere — just not necessarily in this application.
			if (!this.hasSalesArea(basketSalesAreaId)) return;

			this.setSalesAreaId(basketSalesAreaId);
		},
		initState(application: Application) {
			this.applicationNorce = application;

			// Settle the sales area here, so everything downstream can simply read
			// it. A shopper who has chosen a country for this store keeps theirs;
			// everyone else gets this application's default.
			//
			// Nothing has to be cleared first. Another store's area is recorded
			// under that store's locale and is not what this one reads — the
			// question of whose value it is does not arise. What is checked is that
			// the area is one this application still offers, which catches a locale
			// repointed at a different application since the value was stored.
			if (!this.hasSalesArea(this.salesAreaId)) {
				this.setSalesAreaId(application.SalesAreas?.Default?.Id);
			}

			// NOTE: initState does NOT touch countryCode. It is only the checkout
			// delivery-country override, kept per locale like the area. The
			// browsing market is derived live from the application / locale in
			// useGtmEvents' siteMarket(), so a locale switch needs nothing here.
			this.setCurrenciesNorce();
		},
		setCurrenciesNorce() {
			if (this.applicationNorce) {
				this.currenciesNorce = this.applicationNorce?.Currencies?.List;
				const persistedCurrency = this.currenciesNorce.find(
					(currency) => currency.Id === this.currencyId
				);
				const nextCurrency =
					persistedCurrency ??
					this.applicationNorce?.Currencies?.Default ??
					null;
				this.currentCurrency = nextCurrency;
				this.currencyId = nextCurrency?.Id ?? 0;
			}
		},

		updateCurrency(currencyId: number) {
			const selectedCurrency = this.currenciesNorce?.find(
				(c) => c.Id === currencyId
			);
			if (selectedCurrency) {
				this.currentCurrency = selectedCurrency;
				this.currencyId = selectedCurrency.Id;
				return selectedCurrency;
			} else {
				console.error("Currency not found");
				return null;
			}
		},
		initCurrency() {
			if (this.applicationNorce) {
				const nextCurrency =
					this.currentCurrency ??
					this.applicationNorce?.Currencies?.Default ??
					null;
				this.currentCurrency = nextCurrency;
				this.currencyId = nextCurrency?.Id ?? 0;
			}
		},
		addAllParametrics(value: ParametricsNorceStore) {
			this.parametrics = value;
		},
		setProductTypes(value: ProductType[]) {
			this.productTypes = value;
		}
	},
	persist: [
		{
			// `pick`, NOT `paths`: pinia-plugin-persistedstate v4 renamed the option
			// and ignores the old name silently — which persisted the WHOLE store
			// (currency, applicationNorce, parametrics…) and let a stale snapshot
			// overwrite the SSR payload on hydration.
			//
			// `salesAreaId` stays browser-only on purpose: it is the country the
			// shopper picked at checkout, and the price lists are worked out from
			// it on each request rather than stored beside it. Putting it in a
			// cookie would make every response that depends on it uncacheable at
			// the edge, for a correction the browser can make on its own.
			storage: persistedState.localStorage,
			// `checkoutChannel` is the channel the shopper's current order lives on.
			// Persisting it means a reload starts the order lookup on the right
			// channel instead of probing (a 404 per wrong guess), and lets checkout
			// derive the customer type before the order round trip — otherwise it
			// assumes "person" and mounts that type's payment adapter, which then
			// creates a payment on the wrong channel. `resolveOrderByChannel` still
			// validates it against Norce, so a stale value only reorders candidates.
			pick: [
				"salesAreaByLocale",
				"countryCodeByLocale",
				"paymentAdapter",
				"checkoutChannel"
			]
		}
	]
});
